import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, Tab, Tabs } from '@node-space/ui-components'
import { useAddBeneficiaryContext } from 'hooks/useAddBeneficiaryContext'
import { AddBeneficiaryFormPayload, BeneficiaryTransferDestination } from 'types/beneficiaries'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getLocalBankDetailsSummary, getSwiftBankDetails } from 'utils/beneficiaries'
import track from 'utils/tracker'
import { SummaryDetailsList } from './ConfirmationSummary'

type BeneficiarySummaryProps = {
  formState: AddBeneficiaryFormPayload
}

const LocalBeneficiarySummary = ({ formState }: BeneficiarySummaryProps) => {
  const { t } = useTranslation()
  const details = getLocalBankDetailsSummary(formState, t)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.ADD_BENEFICIARY_CONFIRM, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      ...(formState?.currencyCode && { currency: formState?.currencyCode }),
      ...(formState?.iban && { iban: formState?.iban }),
      ...(formState?.beneficiaryPaymentReference && {
        beneficiaryRef: formState?.beneficiaryPaymentReference,
      }),
      ...(formState?.accountNumber && { accountNumber: formState?.accountNumber }),
      ...(formState?.ngnAccountNumber && { ngnAccountNumber: formState?.ngnAccountNumber }),
      ...(formState?.zarAccountNumber && { zarAccountNumber: formState?.zarAccountNumber }),
      ...(formState?.alias && { beneficiaryName: formState?.alias }),
    })
  }, [])

  return (
    <Box data-testid="beneficiaryDetailsSummary" width={'full'}>
      {Object.keys(details ?? {}).map(label => {
        return (
          <DataListItem
            key={label}
            id={label}
            title={label}
            body={details?.[label]}
            layout={'horizontal'}
          />
        )
      })}
    </Box>
  )
}

const InternationalBeneficiarySummary = ({ formState }: BeneficiarySummaryProps) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('beneficiaryDetailsSummary')
  const { beneficiaryDetails, beneficiaryBankDetails, intermediaryBankDetails } =
    getSwiftBankDetails(formState, t)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.ADD_BENEFICIARY_CONFIRM, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      internationalIban: formState?.intlIban,
      beneficiaryRef: formState?.beneficiaryPaymentReference,
    })
  }, [])

  const showIntermediaryBankDetails =
    !formState?.skipIntermediaryBankScreen && Object.keys(intermediaryBankDetails || {}).length

  const tabs: Tab[] = [
    {
      id: 'beneficiaryDetailsSummary',
      header: t('beneficiaryDetails'),
      content: <SummaryDetailsList details={beneficiaryDetails} />,
    },
    {
      id: 'beneficiaryBankDetailsSummary',
      header: t('beneficiaryBankDetails'),
      content: <SummaryDetailsList details={beneficiaryBankDetails} />,
    },
    ...(showIntermediaryBankDetails
      ? [
          {
            id: 'intermediaryBankDetailsSummary',
            header: t('intermediaryBankDetails'),
            content: <SummaryDetailsList details={intermediaryBankDetails} />,
          },
        ]
      : []),
  ]

  return <Tabs tabs={tabs} defaultTabId={activeTab} onChange={setActiveTab} />
}

export const BeneficiarySummary = () => {
  const { beneficiaryFormState } = useAddBeneficiaryContext()
  const isInternationalBeneficiary = useMemo(() => {
    return (
      beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    )
  }, [beneficiaryFormState?.transferDestination])

  const beneficiarySummary = isInternationalBeneficiary ? (
    <InternationalBeneficiarySummary formState={beneficiaryFormState} />
  ) : (
    <LocalBeneficiarySummary formState={beneficiaryFormState} />
  )

  return <>{beneficiarySummary}</>
}
